import { validationConstants } from "constants/formValidation";
import { get } from "lodash";
import { PENDING_ELIGIBILITY_RUNS_FIELD } from "pages/CreateOrder/hooks/eligibilityActivity";
import {
  providerPaymentSetupIncompleteMessage,
  UNLINKED_PAYOUT_PROVIDERS_FIELD,
} from "pages/CreateOrder/utils/payoutProviderSetup";

/**
 * FRAGILITY (known, accepted): getFieldRuleFailure below re-implements react-hook-form's own rule
 * evaluation (`validateField`) and reads its private `control._fields` tree. It mirrors
 * react-hook-form 7.43 (spa/package.json "^7.43.2"; 7.43.9 installed when this was written). A
 * react-hook-form upgrade can change the rule order, the `_fields` shape or what counts as empty
 * without any type error here. The tripwire is usePublishCreateEstimateBlockers.test.tsx, which
 * runs the publisher against a real useForm and checks it against useOrderReadiness — run it on
 * every react-hook-form bump, and re-read `validateField` in node_modules if it fails.
 */

/**
 * CLE-3043 — why "Create and Send" is disabled right now, as short human labels the staff chat
 * widget sends to Clear Health AI (`data.page.blockingIssues`), so "why is the Create button
 * inactive?" gets "Account/Visit ID is required" instead of a guess.
 *
 * The field rules are NOT a parallel list. They are spread over a dozen components as inline
 * `rules={{ … }}` that close over each component's own state (Not scheduled, outreach on/off, the
 * payment-plan switches, the order type…), and which fields exist depends on what is mounted. So
 * the field half reads the rules react-hook-form itself holds (`control._fields`, re-registered by
 * every Controller on every render) and evaluates them the way RHF's own `validateField` does for
 * the first error of each field — the same rules that make `isValid` false. The non-field half is
 * `useOrderReadiness`'s other terms (procedures, the SMS editor, a pending eligibility check) plus
 * the button's `loading` state, which PrimeReact also treats as disabled.
 *
 * Labels only: a field's VALUE never goes into a label. A rule's own message is appended only when
 * it is a fixed sentence (SAFE_RULE_MESSAGES) — several rules build their message from an amount or
 * a date, and those fall back to "<field> is invalid".
 */

export interface IRegisteredFieldRules {
  name: string;
  mount?: boolean;
  disabled?: boolean;
  required?: unknown;
  min?: unknown;
  max?: unknown;
  minLength?: unknown;
  maxLength?: unknown;
  pattern?: unknown;
  validate?: unknown;
}

export interface IFieldRuleFailure {
  name: string;
  kind: "required" | "invalid";
  message: string;
}

export interface ICreateEstimateBlockersOptions {
  /** The form's registered field rules (see collectRegisteredFieldRules). */
  fields: IRegisteredFieldRules[];
  /** react-hook-form's `isValid`. True means no field rule blocks, so none are evaluated. */
  isValid: boolean;
  /** `loadingPriceBreakdown` — the button shows a spinner and cannot be clicked. */
  isPriceLoading?: boolean;
}

/** Field name → how a person would name it. A name missing here is humanised (humaniseFieldName). */
export const CREATE_ESTIMATE_FIELD_LABELS: Record<string, string> = {
  firstName: "Patient first name",
  lastName: "Patient last name",
  dateOfBirth: "Patient date of birth",
  preferredContactMethod: "Preferred contact method",
  phoneNumber: "Patient phone number",
  email: "Patient email",
  orderType: "Estimate type",
  dateOfService: "Date of service",
  timeOfService: "Time of service",
  accountNumber: "Account/Visit ID",
  outOfPocketReason: "Out of pocket reason",
  accountLocationId: "Location",
  estimatedVisitCostForPatient: "Facility patient estimate",
  totalAmount: "Clear Price for patient",
  customText: "SMS message text",
  paymentPlanDurationFull: "Number of payments",
  installmentAmount: "Recurring payment amount",
  installmentFrequency: "Payment frequency",
  paymentPlanMinDownPaymentPercent: "Minimum down payment",
  paymentPlanMinDownPaymentFlat: "Minimum down payment",
  downpaymentOnlyPercent: "Down payment",
  downpaymentOnlyFlatAmount: "Down payment",
  scheduledFirstCommunicationDate: "Scheduled send date",
};

/** Required-rule labels that need more than "<field> is required". */
const REQUIRED_LABEL_OVERRIDES: Record<string, string> = {
  dateOfService: "Date of service is required (or tick Not scheduled)",
};

const flattenMessages = (node: unknown): string[] =>
  typeof node === "string"
    ? [node]
    : Object.values((node as Record<string, unknown>) || {}).flatMap(
        flattenMessages
      );

/**
 * Rule messages that are fixed sentences, so safe to pass on. Everything in validationConstants,
 * plus the fixed returns of the CreateOrder validators (utils/helpers.ts, the payment-plan
 * sections). A message not listed here is dropped, never sent: some carry an amount or a date.
 */
export const SAFE_RULE_MESSAGES: ReadonlySet<string> = new Set([
  ...flattenMessages(validationConstants),
  "Invalid Date",
  "Invalid time.",
  "Email is invalid.",
  "Service date is invalid.",
  "Pick a time at least 2 min from now.",
  "Pick a date no later than 1 day before service date.",
  "Downpayment can not be created for No Balance Due estimates",
  "Percent must be at least 1%",
  "Percent cannot exceed 100%",
  "Amount must be at least $1",
]);

export const NOT_READY_LABELS = {
  NO_PROCEDURES: "At least one procedure is required",
  EMPTY_CPT_DESCRIPTION: "Every procedure needs a description",
  EDITING_MESSAGE: "The SMS message is still being edited (save or cancel it)",
  CHECKING_ELIGIBILITY: "An eligibility check is still running",
  PRICE_LOADING: "The price is still being calculated",
  UNKNOWN_INVALID_FIELD:
    "A field on the page is invalid (look for a red error message)",
};

/** `serviceDetails` term of useOrderReadiness — the button needs at least one procedure. */
export const hasNoProcedures = (procedures: unknown): boolean =>
  !(procedures as unknown[] | null | undefined)?.length;

/** The same per-procedure check useOrderReadiness makes (it throws on a missing description). */
export const isProcedureMissingDescription = (procedure: any): boolean =>
  !procedure.cptDescription.trim().length;

export const humaniseFieldName = (name: string): string => {
  const words = name
    .split(".")
    .filter((part) => !/^\d+$/.test(part))
    .join(" ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "A field";
};

const labelOf = (name: string): string =>
  CREATE_ESTIMATE_FIELD_LABELS[name] ?? humaniseFieldName(name);

const isObjectRule = (rule: unknown): rule is Record<string, unknown> =>
  typeof rule === "object" && rule !== null && !(rule instanceof RegExp);

/** RHF's getValueAndMessage: `{ value, message }` or a bare value with no message. */
const valueAndMessage = (rule: unknown): { value: unknown; message: string } =>
  isObjectRule(rule)
    ? { value: rule.value, message: String(rule.message ?? "") }
    : { value: rule, message: "" };

const isNil = (value: unknown): boolean =>
  value === null || value === undefined;

const isThenable = (value: unknown): boolean =>
  !!value && typeof (value as { then?: unknown }).then === "function";

/** RHF's getValidateError: a string, an all-string array, or `false` is a failure. */
const validateResultMessage = (result: unknown): string | null => {
  if (typeof result === "string") return result;
  if (Array.isArray(result) && result.every((r) => typeof r === "string")) {
    return result.join(" ");
  }
  if (result === false) return "";
  return null;
};

/**
 * The first rule this field fails, in RHF's order (required, min/max, minLength/maxLength, pattern,
 * validate), or null. Mirrors RHF 7.43 `validateField` for Controller-registered fields (their ref is
 * not a DOM input, so no checkbox / radio / file / valueAsNumber cases). A validator that returns a
 * Promise, or throws, is skipped — none on these pages do, and the blockers are computed synchronously.
 */
export const getFieldRuleFailure = (
  field: IRegisteredFieldRules,
  values: Record<string, unknown>
): IFieldRuleFailure | null => {
  const { name, mount, disabled } = field;
  if (!mount || disabled) return null;

  const value = get(values, name);
  const isEmpty = value === "" || (Array.isArray(value) && value.length === 0);
  const fail = (
    kind: IFieldRuleFailure["kind"],
    message: string
  ): IFieldRuleFailure => ({ name, kind, message });

  if (field.required && (isEmpty || isNil(value) || value === false)) {
    const { value: isRequired, message } =
      typeof field.required === "string"
        ? { value: !!field.required, message: field.required }
        : valueAndMessage(field.required);
    if (isRequired) return fail("required", message);
  }

  if (!isEmpty && (!isNil(field.min) || !isNil(field.max))) {
    const min = valueAndMessage(field.min);
    const max = valueAndMessage(field.max);
    if (!isNil(value) && !Number.isNaN(Number(value))) {
      const numeric = value ? +(value as number) : (value as number);
      if (!isNil(max.value) && numeric > (max.value as number)) {
        return fail("invalid", max.message);
      }
      if (!isNil(min.value) && numeric < (min.value as number)) {
        return fail("invalid", min.message);
      }
    }
  }

  if (
    (field.minLength || field.maxLength) &&
    !isEmpty &&
    typeof value === "string"
  ) {
    const minLength = valueAndMessage(field.minLength);
    const maxLength = valueAndMessage(field.maxLength);
    if (
      !isNil(maxLength.value) &&
      value.length > +(maxLength.value as number)
    ) {
      return fail("invalid", maxLength.message);
    }
    if (
      !isNil(minLength.value) &&
      value.length < +(minLength.value as number)
    ) {
      return fail("invalid", minLength.message);
    }
  }

  if (field.pattern && !isEmpty && typeof value === "string") {
    const { value: pattern, message } = valueAndMessage(field.pattern);
    if (pattern instanceof RegExp) {
      // A /g pattern keeps lastIndex between .test calls; match() (what RHF uses) does not.
      if (!value.match(pattern)) return fail("invalid", message);
    }
  }

  let validators: unknown[] = [];
  if (typeof field.validate === "function") validators = [field.validate];
  else if (isObjectRule(field.validate)) {
    validators = Object.values(field.validate);
  }
  for (let i = 0; i < validators.length; i += 1) {
    const validator = validators[i];
    if (typeof validator === "function") {
      let result: unknown = true;
      try {
        result = validator(value, values);
      } catch {
        // A validator that throws is not a reason we can name; RHF would surface it, not us.
      }
      if (!isThenable(result)) {
        const message = validateResultMessage(result);
        if (message !== null) return fail("invalid", message);
      }
    }
  }

  return null;
};

/** The label one failing field contributes. Never contains the field's value. */
export const labelFieldRuleFailure = (failure: IFieldRuleFailure): string => {
  if (failure.kind === "required") {
    return (
      REQUIRED_LABEL_OVERRIDES[failure.name] ??
      `${labelOf(failure.name)} is required`
    );
  }
  const detail = SAFE_RULE_MESSAGES.has(failure.message)
    ? ` (${failure.message})`
    : "";
  return `${labelOf(failure.name)} is invalid${detail}`;
};

/**
 * A string that changes whenever react-hook-form's `formState.errors` gains, loses or changes a
 * field error: each error's dotted path and rule type. RHF mutates the errors object in place, so
 * its reference cannot be used as an effect dependency. `ref` (a DOM element) is never walked.
 */
export const formErrorsSignature = (errors: unknown, path = ""): string => {
  if (!errors || typeof errors !== "object") return "";
  const node = errors as Record<string, unknown>;
  const own = typeof node.type === "string" ? `${path}:${node.type};` : "";
  return (
    own +
    Object.keys(node)
      // A FieldError's own `type` / `message` are strings and add nothing; a field NAMED "type" is
      // an object and is walked. `ref` is a DOM element.
      .filter((key) => key !== "ref" && key !== "types")
      .map((key) =>
        formErrorsSignature(node[key], path ? `${path}.${key}` : key)
      )
      .join("")
  );
};

/**
 * Flattens RHF's `control._fields` tree (a dotted name such as `a.b` is stored nested) into the
 * registered fields' rules. A node with `_f` is a field; anything else is a branch.
 */
export const collectRegisteredFieldRules = (
  tree: unknown
): IRegisteredFieldRules[] => {
  const out: IRegisteredFieldRules[] = [];
  const walk = (node: unknown) => {
    if (!node || typeof node !== "object") return;
    const { _f: rules, ...children } = node as Record<string, unknown>;
    if (rules && typeof rules === "object" && "name" in rules) {
      out.push(rules as IRegisteredFieldRules);
    }
    Object.values(children).forEach(walk);
  };
  walk(tree);
  return out;
};

/**
 * Every reason "Create and Send" is disabled, in page order (field rules first, then the
 * readiness terms). An empty list means nothing is blocking. Labels only.
 */
export function getCreateEstimateBlockers(
  values: Record<string, unknown>,
  { fields, isValid, isPriceLoading = false }: ICreateEstimateBlockersOptions
): string[] {
  const issues: string[] = [];

  if (!isValid) {
    const fieldIssues = fields
      .map((field) => getFieldRuleFailure(field, values))
      .filter((failure): failure is IFieldRuleFailure => !!failure)
      .map(labelFieldRuleFailure);
    // isValid is false but no rule fails here: a rule we could not evaluate (an async validator).
    issues.push(
      ...(fieldIssues.length
        ? fieldIssues
        : [NOT_READY_LABELS.UNKNOWN_INVALID_FIELD])
    );
  }

  const procedures = values.serviceDetails as unknown[] | null | undefined;
  if (hasNoProcedures(procedures)) {
    issues.push(NOT_READY_LABELS.NO_PROCEDURES);
  } else if (
    procedures?.some(
      (procedure: any) =>
        typeof procedure?.cptDescription !== "string" ||
        isProcedureMissingDescription(procedure)
    )
  ) {
    issues.push(NOT_READY_LABELS.EMPTY_CPT_DESCRIPTION);
  }

  if (values.isEditingMessage) issues.push(NOT_READY_LABELS.EDITING_MESSAGE);
  if (Number(values[PENDING_ELIGIBILITY_RUNS_FIELD]) > 0) {
    issues.push(NOT_READY_LABELS.CHECKING_ELIGIBILITY);
  }
  // CLE-3074 — a provider's name is configuration, not a patient value, so it may go into a label.
  const unlinkedPayoutProviders = values[UNLINKED_PAYOUT_PROVIDERS_FIELD];
  if (Array.isArray(unlinkedPayoutProviders)) {
    unlinkedPayoutProviders
      .filter((name): name is string => typeof name === "string")
      .forEach((name) =>
        issues.push(providerPaymentSetupIncompleteMessage(name))
      );
  }
  if (isPriceLoading) issues.push(NOT_READY_LABELS.PRICE_LOADING);

  return Array.from(new Set(issues));
}
